import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Database, Flame, LogOut, X } from 'lucide-react';

interface NavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  showAdminLink?: boolean;
  userEmail?: string | null;
  onLogout?: () => void;
}

const NAV_ITEMS = [
  { label: 'HEATMAPS', to: '/' },
  { label: 'TRINITY', to: '/trinity' },
] as const;

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (container == null) {
    return [];
  }

  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
    ),
  );
}

interface DrawerLinkProps {
  label: string;
  to: string;
  isActive: boolean;
  onClose: () => void;
}

function DrawerLink({ label, to, isActive, onClose }: DrawerLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClose}
      aria-current={isActive ? 'page' : undefined}
      className={`relative flex w-full items-center py-[7px] pl-[22px] pr-3 font-mono text-[13px] tracking-[0.04em] before:absolute before:left-0 before:top-1/2 before:w-3 before:border-t before:border-[#555] ${
        isActive ? 'font-medium text-fg' : 'text-dim hover:text-soft'
      }`}
    >
      <span>{label}</span>
      {isActive && (
        <span className="ml-[7px] inline-block h-[15px] w-2 animate-[blink_1s_ease-in-out_infinite] rounded-[1px] bg-[#90bff9] shadow-[0_0_8px_rgba(144,191,249,0.5)]" />
      )}
    </Link>
  );
}

function DrawerGroup({
  icon,
  label,
  className = '',
}: {
  icon: ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center gap-2.5 px-3.5 py-1.5 font-mono text-[13px] font-semibold tracking-[0.08em] text-fg ${className}`}
    >
      {icon}
      {label}
    </div>
  );
}

export function NavDrawer({
  isOpen,
  onClose,
  showAdminLink = false,
  userEmail,
  onLogout,
}: NavDrawerProps) {
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const hasEmail = userEmail != null && userEmail !== '';

  // While open, act as a modal dialog: move focus into the drawer, trap Tab
  // inside it, and restore focus to the trigger (e.g. the hamburger) on close.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    getFocusableElements(drawerRef.current)[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(drawerRef.current);
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const focusInsideDrawer = drawerRef.current?.contains(active) ?? false;

      if (event.shiftKey) {
        if (active === first || !focusInsideDrawer) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-[45] bg-black/60 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <div
        ref={drawerRef}
        aria-label="Navigation"
        role="dialog"
        aria-modal="true"
        inert={!isOpen}
        className={`fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r leading-[normal] border-line-strong bg-background transition-transform duration-200 ${
          isOpen ? 'translate-x-0 shadow-[20px_0_40px_rgba(0,0,0,0.5)]' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center gap-2.5 border-b border-line p-3">
          <img
            src="/brand/meridian-view-ai-mark.webp"
            alt=""
            aria-hidden="true"
            className="block h-[30px] w-auto shrink-0 mix-blend-lighten"
          />
          <span className="font-sans text-[15px] font-semibold text-fg">MeridianViewAI</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto grid h-7 w-7 place-items-center rounded-md text-soft hover:bg-white/[.08]"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto pt-2.5">
          <DrawerGroup
            icon={<Flame className="h-[15px] w-[15px]" strokeWidth={1.8} />}
            label="HEATSEEKER"
          />
          <div className="relative ml-[21px] border-l border-[#555]">
            {NAV_ITEMS.map((item) => (
              <DrawerLink
                key={item.to}
                label={item.label}
                to={item.to}
                isActive={location.pathname === item.to}
                onClose={onClose}
              />
            ))}
          </div>

          {showAdminLink && (
            <>
              <DrawerGroup
                icon={<Database className="h-[15px] w-[15px]" strokeWidth={1.8} />}
                label="SYSTEM"
                className="mt-3.5"
              />
              <div className="relative ml-[21px] border-l border-[#555]">
                <DrawerLink
                  label="ADMIN"
                  to="/admin"
                  isActive={location.pathname === '/admin'}
                  onClose={onClose}
                />
              </div>
            </>
          )}
        </nav>

        {(hasEmail || onLogout != null) && (
          <div className="flex flex-col gap-2 border-t border-line p-3">
            {hasEmail && (
              <span title={userEmail} className="truncate font-mono text-[11px] text-dim">
                {userEmail}
              </span>
            )}
            {onLogout != null && (
              <button
                type="button"
                onClick={onLogout}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-line-strong bg-transparent px-3 py-[7px] text-xs text-subtle hover:bg-white/[.04] hover:text-fg"
              >
                <LogOut className="h-[13px] w-[13px]" strokeWidth={2} />
                <span>Sign Out</span>
              </button>
            )}
          </div>
        )}

        <div className="border-t border-line px-5 py-3 font-mono text-[9px] leading-[1.6] tracking-[0.08em] text-dim">
          <div>MERIDIAN VIEW AI</div>
          <div>TERMINAL v1.0</div>
        </div>
      </div>
    </>
  );
}
